/**
 * Scope helpers for ad-creative backend task documents.
 *
 * Task docs carry their target entity in two places: the typed
 * `targetEntityPath.firestorePath` and the untyped `request` payload written by
 * the builder services. These helpers extract the brief/item scope so surfaces
 * can filter useTaskTransition callbacks to their own entity.
 */

import type { BackendTask } from '@modules/background-tasks';

export const AD_CREATIVE_VIDEO_TASK_TYPE = 'ad-creative-video-generation';

const ITEM_PATH_RE = /\/ad_creative_items\/([^/]+)$/;

interface TaskRequestScope {
  request?: { itemId?: unknown; briefId?: unknown };
}

export function isAdCreativeTask(task: BackendTask): boolean {
  return Boolean(task.taskType?.startsWith('ad-creative'));
}

/** Video-generation task check. The type is written by the backend as a raw
 * string and is not part of the schema AgentTaskType union — compare widened. */
export function isAdCreativeVideoTask(task: BackendTask): boolean {
  return String(task.taskType ?? '') === AD_CREATIVE_VIDEO_TASK_TYPE;
}

/** Item id targeted by a video-generation task (path first, request fallback). */
export function adCreativeTaskItemId(task: BackendTask): string | null {
  const path = task.targetEntityPath?.firestorePath;
  const fromPath = path ? ITEM_PATH_RE.exec(path)?.[1] : undefined;
  if (fromPath) return fromPath;
  const request = (task as unknown as TaskRequestScope).request;
  return typeof request?.itemId === 'string' ? request.itemId : null;
}

/** Brief id a task was started for, when the task recorded one. */
export function adCreativeTaskBriefId(task: BackendTask): string | null {
  const request = (task as unknown as TaskRequestScope).request;
  return typeof request?.briefId === 'string' ? request.briefId : null;
}
